import type { CataloguePropInfo, CatalogueTilesetInfo } from "../editor/catalogue.ts";
import { tilePropFrameSize } from "./layout.ts";
import { TILE_PROPS_TILESET, tilePropsTilesetId, type TileProps, type TilePropEntry } from "./types.ts";

/**
 * A pack's tile props as the rest of the editor sees them (ADR-102 decision 7): the collection-shaped tileset a
 * pack gains, which keeps every entry so a retired one still draws, and the palette entries, which leave retired
 * ones out.
 */

/**
 * The image a collection tile names. `source` is the entry's place in the document, not a file: the build composes
 * the frame from its tiles and reads no image for it.
 */
export interface TilePropsTileImage {
  readonly source: string;
  readonly width: number;
  readonly height: number;
}

/**
 * One tile of the tile props tileset: an entry's frame, plain data of the shape an imported collection tile has.
 */
export interface TilePropsTile {
  readonly id: number;
  readonly probability: number;
  readonly image: TilePropsTileImage;
  readonly animation: readonly never[];
  readonly collision: readonly never[];
}

/**
 * The tile props tileset: the shape of an imported image collection (no grid, no tileset image, one tile with its
 * own image for each entry), with `tileWidth` and `tileHeight` the largest of its tiles', as Tiled records a
 * collection.
 */
export interface TilePropsTilesetDefinition {
  readonly id: string;
  readonly name: string;
  readonly tileWidth: number;
  readonly tileHeight: number;
  readonly tileCount: number;
  readonly columns: 0;
  readonly image: null;
  readonly tiles: readonly TilePropsTile[];
}

/**
 * What a pack's tile props add to the catalogue: the tileset listed with role `props`, and the entries the palette
 * offers.
 */
export interface TilePropsCatalogue {
  readonly tileset: CatalogueTilesetInfo;
  readonly props: readonly CataloguePropInfo[];
}

/**
 * The tile props tileset of a pack, every entry in it, retired ones included.
 *
 * @param document The pack's document.
 * @param tileSizeOf The tile size of an entry's tilesets in pixels, which the host knows from the pack.
 * @returns The definition.
 * @throws Error when a tile size is not a positive whole number.
 */
export function tilePropsTilesetDefinition(document: TileProps, tileSizeOf: (entry: TilePropEntry) => number): TilePropsTilesetDefinition {
  const tiles = [...document.props]
    .sort((a, b) => a.id - b.id)
    .map((entry): TilePropsTile => {
      const { width, height } = tilePropFrameSize(entry, tileSizeOf(entry));
      return { id: entry.id, probability: 1, image: { source: `${TILE_PROPS_TILESET}/${entry.id}`, width, height }, animation: [], collision: [] };
    });
  return {
    id: tilePropsTilesetId(document.pack),
    name: TILE_PROPS_TILESET,
    tileWidth: Math.max(0, ...tiles.map((tile) => tile.image.width)),
    tileHeight: Math.max(0, ...tiles.map((tile) => tile.image.height)),
    tileCount: tiles.length,
    columns: 0,
    image: null,
    tiles,
  };
}

/**
 * The catalogue entries of a pack's tile props: the tileset with role `props`, and one static prop for each entry
 * that is not retired, in the folder `tile-props/<group>`.
 *
 * @param document The pack's document.
 * @returns The tileset and the palette entries, by id.
 */
export function tilePropsCatalogue(document: TileProps): TilePropsCatalogue {
  const tileset = tilePropsTilesetId(document.pack);
  return {
    tileset: { id: tileset, name: TILE_PROPS_TILESET, role: "props" },
    props: [...document.props]
      .filter((entry) => entry.retired !== true)
      .sort((a, b) => a.id - b.id)
      .map((entry) => ({ tileset, tile: entry.id, name: entry.name, group: entry.group, path: `${TILE_PROPS_TILESET}/${entry.group}`, plays: "static" as const })),
  };
}
